import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { getProducts } from '../../clients/product_client';
import { getShopCatalog } from '../../clients/shop_client';
import { AppContext } from '../../App';
import './ProductPageStyle.css';
import { showToast } from '../../config_and_helpers/helpers';
import {
  logListSearchEvent,
  logListSearchError,
} from '../../config_and_helpers/logger';
import ProductSearchBarComponent from '../../components/Product/ProductSearchBarComponent';
import ProductInfiniteScrollComponent from '../../components/Product/ProductInfiniteScrollComponent';
import ProductFilterComponent from '../../components/Product/ProductFilterComponent';
import { fetchCurrencies } from '../../clients/currency_client';
import {
  areNextPageParamsCompatible,
  normalizeParam,
  resolveSupplierCatalogueFilter,
} from './productListPaginationUtils';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';
import {
  abortListSearchController,
  isPartialListSearchQuery,
  isRunnableListSearchQuery,
  resetPaginationForPartialSearch,
} from '../../utils/listSearchRequest';
import {
  getShopListKey,
  prepareShopCatalogForList,
} from '../../utils/shopCatalog';

// Context passing on the total product list and it setProducts function in order to update said array
// such that updates occur both on the server side and on the client side without needing to
// re-fetch the updated data from the server
export const ProductContext = createContext();

/**
 * ProductList is a component that handles state and context management for displaying a list of products.
 *
 * @param {boolean} isSupplier - Specifies if the user is a supplier
 * @param {boolean} isShopView - Specifies if the user is viewing products in a shop
 * @param {boolean} isCatalogueView - Legacy shop tab hint (maps to supplier_cat_item client filter)
 * @param {string} [shopCatalogFilter=''] - Initial supplier_cat_item filter for shop ('' | 'true' | 'false')
 * @param {boolean} similarProductsView - Specifies if the user is viewing similar products
 * @param {Array} similarProducts - Array of similar product objects
 * @param {function} setSimilarProducts - Function to set the similar products array
 * @returns {JSX.Element} - JSX element containing the product search bar and infinite scroll components
 */

const ProductList = ({
  isSupplier = false,
  isShopView = false,
  isCatalogueView = false,
  shopCatalogFilter = '',
  similarProductsView = false,
  similarProducts,
  setSimilarProducts,
}) => {
  // State and context management.
  const { token } = useContext(AppContext);
  const isMountedRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null); // Used to cancel previous requests
  const fetchProductsRef = useRef(null);
  const nextPageUrlRef = useRef(null);
  const shopCatalogRawRef = useRef([]);
  const productsRef = useRef([]);

  const [products, setProducts] = useState([]);

  // Update products ref when state changes
  useEffect(() => {
    productsRef.current = products;
  }, [products]);
  const [supplier, setSupplier] = useState('');
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [currencyRates, setCurrencyRates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isSearching, setIsSearching] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [filters, setFilters] = useState({
    category: '',
    unit: '',
    storage: '',
    currency: '',
    expiryRelevant: '',
    minPrice: '',
    maxPrice: '',
    minStock: '',
    maxStock: '',
    supplierCatalogue: shopCatalogFilter || (isCatalogueView ? 'true' : ''),
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const mergeAndSortProducts = useCallback(
    incoming => {
      const dedupedMap = new Map();
      (incoming || []).forEach(product => {
        if (!product) return;
        const key = isShopView ? getShopListKey(product) : product.id;
        if (key != null) {
          dedupedMap.set(key, product);
        }
      });
      const merged = Array.from(dedupedMap.values());
      if (!sortBy) {
        return merged;
      }
      const directionFactor = sortDirection === 'asc' ? 1 : -1;
      merged.sort((a, b) => {
        const aValue = a?.[sortBy];
        const bValue = b?.[sortBy];
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1 * directionFactor;
        if (bValue == null) return -1 * directionFactor;
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return (aValue - bValue) * directionFactor;
        }
        return String(aValue).localeCompare(String(bValue)) * directionFactor;
      });
      return merged;
    },
    [sortBy, sortDirection, isShopView]
  );

  const applyShopCatalogFilters = useCallback(
    rawEntries => {
      const filtered = prepareShopCatalogForList(rawEntries, {
        search: searchInput,
        supplierCatalogue: filters.supplierCatalogue,
        supplierId: supplier,
      });
      return mergeAndSortProducts(filtered);
    },
    [searchInput, filters.supplierCatalogue, supplier, mergeAndSortProducts]
  );

  // Rests the search input value. passed down as prop to the children components to be used when necessary
  const resetSearchValue = () => {
    setSearchInput('');
  };

  const getCurrencyRates = useCallback(() => {
    fetchCurrencies(token).then(response => {
      if (response.success) {
        setCurrencyRates(response.data);
      }
    });
  }, [token]);

  useEffect(() => {
    getCurrencyRates();
  }, [getCurrencyRates]);

  // Keep the ref synchronized with the state
  useEffect(() => {
    nextPageUrlRef.current = nextPageUrl;
  }, [nextPageUrl]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clean up debounce timeout and requests
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }
    };
  }, []);

  // Function to fetch products based on certain parameters.
  const fetchProducts = useCallback(
    (newSearch = false) => {
      const trigger = newSearch ? 'search-or-filter' : 'scroll-or-refresh';

      // Cancel previous request if it exists
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }

      // Set loading state
      setIsLoading(true);
      if (newSearch) {
        setProducts([]); // Clear products for new search
        productsRef.current = []; // Also clear the ref
      }

      // Create a cancelable promise
      const controller = new AbortController();
      currentRequestRef.current = controller;

      const supplierCatalogueFilterValue = resolveSupplierCatalogueFilter({
        supplierCatalogueFilter: filters.supplierCatalogue,
        isShopView,
        isCatalogueView,
      });

      const requestParams = {
        searchInput: searchInput,
        supplierId: supplier,
        supplierCatalogue: isCatalogueView,
        nextPage: newSearch ? null : nextPageUrlRef.current,
        category: filters.category,
        unit: filters.unit,
        storage: filters.storage,
        currency: filters.currency,
        expiryRelevant: filters.expiryRelevant,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        minStock: filters.minStock,
        maxStock: filters.maxStock,
        supplierCatalogueFilter: supplierCatalogueFilterValue,
        sortBy,
        sortDirection,
      };

      logListSearchEvent('products', 'request', {
        trigger,
        params: requestParams,
        shopMode: isShopView,
      });

      if (isShopView) {
        getShopCatalog(token, { signal: controller.signal })
          .then(response => {
            if (!isMountedRef.current) return;
            if (currentRequestRef.current !== controller) return;

            if (response?.canceled) {
              setIsLoading(false);
              setIsSearching(false);
              currentRequestRef.current = null;
              return;
            }

            if (response.success) {
              shopCatalogRawRef.current = response.data || [];
              const pageItems = applyShopCatalogFilters(shopCatalogRawRef.current);
              setProducts(pageItems);
              productsRef.current = pageItems;
              setHasMore(false);
              nextPageUrlRef.current = null;
              setNextPageUrl(null);

              logListSearchEvent('products', 'success', {
                trigger,
                shopMode: true,
                responseMeta: {
                  success: true,
                  count: pageItems.length,
                  rawCount: shopCatalogRawRef.current.length,
                },
              });
            } else {
              setHasMore(false);
              showToast(
                'Failed to load shop catalog. Please try again.',
                'error',
                'top-center',
                3000
              );
              logListSearchEvent('products', 'failure', {
                trigger,
                shopMode: true,
                status: response.status,
              });
            }

            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
          })
          .catch(error => {
            if (currentRequestRef.current !== controller) return;
            if (
              error?.name === 'AbortError' ||
              error?.code === 'ERR_CANCELED'
            ) {
              setIsLoading(false);
              setIsSearching(false);
              currentRequestRef.current = null;
              return;
            }
            if (!isMountedRef.current) return;
            logListSearchError('products', trigger, error, { shopMode: true });
            setHasMore(false);
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
          });
        return;
      }

      getProducts(token, setProducts, { ...requestParams, signal: controller.signal })
        .then(response => {
          if (!isMountedRef.current) return;
          if (currentRequestRef.current !== controller) {
            return;
          }

          if (response?.canceled) {
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          const responseMeta = {
            success: response?.success,
            reachedEnd: !!response?.reachedEnd,
            nextPage: response?.nextPage,
          };

          if (response && response.success) {
            if (response.reachedEnd) {
              setHasMore(false);
              nextPageUrlRef.current = null;
              setNextPageUrl(null);
            } else {
              setHasMore(true);
              nextPageUrlRef.current = response.nextPage;
              setNextPageUrl(response.nextPage);
            }
          } else {
            setHasMore(false);
            nextPageUrlRef.current = null;
            setNextPageUrl(null);
            showToast(
              'An unexpected error occurred. Please try again',
              'error',
              'top-center',
              3000
            );
          }

          logListSearchEvent(
            'products',
            response && response.success ? 'success' : 'failure',
            {
              trigger,
              params: requestParams,
              responseMeta,
            }
          );

          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        })
        .catch(error => {
          if (currentRequestRef.current !== controller) {
            return;
          }
          if (error.name === 'AbortError') {
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          if (!isMountedRef.current) return;

          logListSearchError('products', trigger, error, {
            params: requestParams,
          });

          setHasMore(false);
          nextPageUrlRef.current = null;
          setNextPageUrl(null);
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        });
    },
    [
      token,
      searchInput,
      supplier,
      isCatalogueView,
      isShopView,
      filters,
      sortBy,
      sortDirection,
      applyShopCatalogFilters,
    ]
  );

  const validateNextPageUrl = useCallback(() => {
    const nextPage = nextPageUrlRef.current;
    if (!nextPage) {
      return { valid: true };
    }

    try {
      const parsed = new URL(nextPage, window.location.origin);
      const urlParams = {
        search: normalizeParam(parsed.searchParams.get('search')),
        supplier_id: normalizeParam(parsed.searchParams.get('supplier_id')),
        supplier_catalogue: normalizeParam(
          parsed.searchParams.get('supplier_catalogue')
        ),
        category: normalizeParam(parsed.searchParams.get('category')),
        unit: normalizeParam(parsed.searchParams.get('unit')),
        storage: normalizeParam(parsed.searchParams.get('storage')),
        currency: normalizeParam(parsed.searchParams.get('currency')),
        expiry_relevant: normalizeParam(
          parsed.searchParams.get('expiry_relevant')
        ),
        min_price: normalizeParam(parsed.searchParams.get('min_price')),
        max_price: normalizeParam(parsed.searchParams.get('max_price')),
        min_stock: normalizeParam(parsed.searchParams.get('min_stock')),
        max_stock: normalizeParam(parsed.searchParams.get('max_stock')),
        supplier_catalogue_filter: normalizeParam(
          parsed.searchParams.get('supplier_catalogue_filter') ||
            resolveSupplierCatalogueFilter({
              supplierCatalogueFilter: filters.supplierCatalogue,
              isShopView,
              isCatalogueView,
            })
        ),
        sort_by: normalizeParam(parsed.searchParams.get('sort_by')),
        sort_direction: normalizeParam(parsed.searchParams.get('sort_direction')),
      };

      const stateParams = {
        search: normalizeParam(searchInput),
        supplier_id: normalizeParam(supplier),
        supplier_catalogue: normalizeParam(isCatalogueView ? true : ''),
        category: normalizeParam(filters.category),
        unit: normalizeParam(filters.unit),
        storage: normalizeParam(filters.storage),
        currency: normalizeParam(filters.currency),
        expiry_relevant: normalizeParam(filters.expiryRelevant),
        min_price: normalizeParam(filters.minPrice),
        max_price: normalizeParam(filters.maxPrice),
        min_stock: normalizeParam(filters.minStock),
        max_stock: normalizeParam(filters.maxStock),
        supplier_catalogue_filter: resolveSupplierCatalogueFilter({
          supplierCatalogueFilter: filters.supplierCatalogue,
          isShopView,
          isCatalogueView,
        }),
        sort_by: normalizeParam(sortBy),
        sort_direction: normalizeParam(sortDirection),
      };

      const staleReasons = Object.keys(stateParams).filter(
        key =>
          !areNextPageParamsCompatible({
            key,
            urlValue: urlParams[key],
            stateValue: stateParams[key],
            isShopView,
            isCatalogueView,
          })
      );

      if (staleReasons.length > 0) {
        return {
          valid: false,
          staleReasons,
          urlParams,
          stateParams,
          nextPageUrl: nextPage,
        };
      }

      return { valid: true };
    } catch (error) {
      return {
        valid: false,
        parseError: error,
        nextPageUrl: nextPage,
      };
    }
  }, [
    searchInput,
    supplier,
    isCatalogueView,
    filters,
    sortBy,
    sortDirection,
    isShopView,
  ]);

  const handleLoadMoreProducts = useCallback(() => {
    if (isShopView) {
      return;
    }
    if (isPartialListSearchQuery(searchInput)) {
      return;
    }

    if (!nextPageUrlRef.current) {
      return;
    }
    const validation = validateNextPageUrl();
    if (!validation.valid) {
      if (validation.parseError) {
        logListSearchError('products', 'next-page-url-parse-error', validation.parseError, {
          nextPageUrl: validation.nextPageUrl,
        });
      } else {
        logListSearchEvent('products', 'stale-next-page-url', {
          staleReasons: validation.staleReasons,
          urlParams: validation.urlParams,
          stateParams: validation.stateParams,
          nextPageUrl: validation.nextPageUrl,
        });
      }
      setHasMore(false);
      nextPageUrlRef.current = null;
      setNextPageUrl(null);
      return;
    }
    fetchProducts(false);
  }, [fetchProducts, validateNextPageUrl, isShopView, searchInput]);

  // Store the current fetchProducts function in a ref
  fetchProductsRef.current = fetchProducts;

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    setIsLoading(true); //  Show spinner immediately

    // Set searching state for search input changes
    if (searchInput.length >= 2) {
      setIsSearching(true);
    }

    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Only search if we have 2+ characters or empty string
    if (isRunnableListSearchQuery(searchInput)) {
      debounceTimeoutRef.current = setTimeout(() => {
        if (!fetchProductsRef.current) return;
        if (isShopView && shopCatalogRawRef.current.length > 0) {
          const pageItems = applyShopCatalogFilters(shopCatalogRawRef.current);
          setProducts(pageItems);
          productsRef.current = pageItems;
          setIsLoading(false);
          setIsSearching(false);
          return;
        }
        fetchProductsRef.current(true);
      }, 300);
    } else if (isPartialListSearchQuery(searchInput)) {
      setProducts([]);
      productsRef.current = [];
      setIsSearching(false);
      resetPaginationForPartialSearch({
        setHasMore,
        setNextPageUrl,
        nextPageUrlRef,
        abortControllerRef: currentRequestRef,
      });
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [
    supplier,
    searchInput,
    isCatalogueView,
    filters,
    sortBy,
    sortDirection,
    isShopView,
    applyShopCatalogFilters,
  ]);

  const handleApplyFilters = () => {
    setNextPageUrl(null);
    fetchProducts(true); // newSearch = true for filter changes
  };

  const handleClearFilters = () => {
    setNextPageUrl(null);
    fetchProducts(true); // newSearch = true for filter changes
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const isInventoryGlassView =
    !isShopView && !isCatalogueView && !similarProductsView;

  const listContent = (
    <>
      <div className={isInventoryGlassView ? 'glass-toolbar' : undefined}>
        <ProductSearchBarComponent
          isCatalogueView={isCatalogueView}
          isShopView={isShopView}
          useGlassToolbar={isInventoryGlassView}
          fetchProducts={fetchProducts}
          setNextPageUrl={setNextPageUrl}
          supplier={supplier}
          setSupplier={setSupplier}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          showFilters={showFilters}
          toggleFilters={toggleFilters}
        />
        {showFilters && isInventoryGlassView ? (
          <ProductFilterComponent
            filters={filters}
            setFilters={setFilters}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            useGlassPanel
            embeddedInToolbar
            isShopView={isShopView}
          />
        ) : null}
      </div>
      {showFilters && !isInventoryGlassView ? (
        <ProductFilterComponent
          filters={filters}
          setFilters={setFilters}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          isShopView={isShopView}
        />
      ) : null}
      <ProductInfiniteScrollComponent
        isSupplier={isSupplier}
        isShopView={isShopView}
        isCatalogueView={isCatalogueView}
        similarProductsView={similarProductsView}
        products={products}
        resetSearchValue={resetSearchValue}
        fetchProducts={fetchProducts}
        onLoadMore={handleLoadMoreProducts}
        similarProducts={similarProducts}
        setSimilarProducts={setSimilarProducts}
        isLoading={isLoading}
        isSearching={isSearching}
        hasMore={hasMore}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={(field, direction) => {
          setSortBy(field);
          setSortDirection(direction);
        }}
        useGlassTable={isInventoryGlassView}
        searchInput={searchInput}
      />
    </>
  );

  return (
    <ProductContext.Provider value={{ products, setProducts, currencyRates }}>
      {isInventoryGlassView ? (
        <GlassWorkspaceShell theme="neutral" wide>
          {listContent}
        </GlassWorkspaceShell>
      ) : (
        listContent
      )}
    </ProductContext.Provider>
  );
};
export default ProductList;
